import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { gsap } from 'gsap';
import { RsMotionService, RsRevealKind } from './rs-motion';

/**
 * Drop-in scroll reveal for the design system.
 *
 * ```html
 * <section rsReveal>…</section>                        <!-- fade up -->
 * <div rsReveal="scale" rsRevealStagger=".rs-card"></div>
 * <h1 rsReveal="up" rsRevealImmediate>…</h1>           <!-- on load -->
 * ```
 *
 * Purely additive: it never changes layout, only animates transform/opacity.
 */
@Directive({
  selector: '[rsReveal]',
  standalone: true,
})
export class RsRevealDirective implements AfterViewInit, OnDestroy {
  /** Reveal character. Empty value falls back to `up`. */
  @Input('rsReveal') kind: RsRevealKind | '' = 'up';

  /** Optional child selector — those children stagger in instead of the host. */
  @Input() rsRevealStagger?: string;

  /** Seconds between staggered children. */
  @Input() rsRevealGap: number | string = 0.08;

  /** Seconds. */
  @Input() rsRevealDuration: number | string = 0.7;

  /** Seconds. */
  @Input() rsRevealDelay: number | string = 0;

  /** ScrollTrigger start position. */
  @Input() rsRevealStart = 'top 86%';

  /** Play immediately on view init rather than on scroll. */
  @Input({ transform: (v: unknown) => v !== false && v !== 'false' })
  rsRevealImmediate = false;

  private ctx?: gsap.Context;

  constructor(
    private host: ElementRef<HTMLElement>,
    private motion: RsMotionService,
    private zone: NgZone
  ) {}

  ngAfterViewInit(): void {
    const el = this.host.nativeElement;
    const kind = (this.kind || 'up') as RsRevealKind;

    const targets = this.rsRevealStagger
      ? Array.from(el.querySelectorAll<HTMLElement>(this.rsRevealStagger))
      : [el];

    if (!targets.length) return;

    // Mark the elements so the CSS pre-hide rule can take effect.
    targets.forEach((t) => t.setAttribute('data-rs-reveal', kind));

    if (!this.motion.enabled) {
      targets.forEach((t) => t.classList.add('rs-revealed'));
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.ctx = this.motion.context(el, () => {
        this.motion.reveal(targets, {
          kind,
          stagger: Number(this.rsRevealGap),
          duration: Number(this.rsRevealDuration),
          delay: Number(this.rsRevealDelay),
          start: this.rsRevealStart,
          immediate: this.rsRevealImmediate,
          trigger: el,
        });
      });
    });
  }

  ngOnDestroy(): void {
    this.ctx?.revert();
  }
}
